import { useEffect } from "react";
import {
  CITATION_ATTR,
  DEAD_LINK_ATTR,
  EXTERNAL_OK_ATTR,
  LINK_ELEMENTS,
  isNavigatingHref,
} from "./kbArticleHtml";

/**
 * The click interception every surface that renders knowledge-base HTML must run (ACOP-101).
 *
 * ── Why this is a shared hook ────────────────────────────────────────────────────────────────────
 *
 * The hard requirement is that **no click inside injected content leaves Copilot** (ACOP-93). Copilot
 * runs in the Salesforce utility-bar iframe, so leaving Copilot means leaving the ERP.
 * `prepareArticleHtml` has already neutralised anything that could navigate, which means the
 * fail-closed branch at the bottom should be unreachable — it is here because "should be
 * unreachable" is not a guarantee, and the cost of being wrong is a customer-facing app dropping the
 * reader onto another site.
 *
 * This logic was written twice, in CitationTwo and KnowledgeBaseTwo, at ~70 lines each. Home would
 * have been the third copy. `kbArticleFetch.ts`'s own header warns that "a second hand-written copy
 * of it would drift, quietly" — a third is how the guarantee above stops being one.
 *
 * ⚠️ **Today this hook has exactly ONE consumer: Home.** The two originals were deliberately left
 * untouched (ACOP-101 did not take on behaviour-parity risk for two shipped surfaces it was not
 * changing), so the duplication is *capped*, not removed. Migrating them is worth doing and is not
 * this hook's own justification — read the paragraph above as "why Home did not fork it", not as a
 * claim that three callers now share it.
 *
 * Two things to carry across when someone does migrate:
 *   1. **KnowledgeBaseTwo SWALLOWS a citation whose path yields no urlname** — `if (next)
 *      onOpenArticle(next)`. This hook always calls `onCitation`, so that guard has to move into
 *      KB's callback or a click that used to do nothing will start navigating.
 *   2. `escapeSelectorValue` is exported below, but **both components still carry their own copy** —
 *      so extracting this made it three, in the very file arguing against third copies. Point them
 *      at this one as part of the migration.
 *
 * ── Why the destination is a callback, not a behaviour ───────────────────────────────────────────
 *
 * The two existing copies are NOT identical, and the difference is exactly one branch: what to do
 * with a resolved citation. The slide-out pushes a history entry and stays where it is; the KB page
 * converts the path to an identifier and re-routes itself. Everything else — fragment scrolling,
 * dead links, missing hrefs, bare "#", the fail-closed tail — really is duplicated.
 *
 * So the hook owns the parts that must never differ and takes the destination as `onCitation`. It
 * deliberately does NOT unify on either caller's shape: making Home route through the KB page's
 * identifier conversion would add a validation hop nobody chose, on the one value whose shape is
 * most likely to change (AVSOS emits `/api/public/knowledge/<UUID>/html` today, not urlnames).
 */

/**
 * CSS.escape with a fallback.
 *
 * Every branch that reaches a selector has already called preventDefault, so a throw here cannot let
 * a click escape — but it would still break in-page scrolling, and CSS.escape is absent under jsdom,
 * which is where these components are tested. Quotes and backslashes are what matter: the value is
 * interpolated into an `[id="…"]` selector.
 */
export function escapeSelectorValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/["\\]/g, "\\$&");
}

export type UseCitationClicksOptions = {
  /** The element the content is rendered into. Clicks are captured here, not on the document. */
  containerRef: { current: HTMLElement | null };
  /**
   * The rendered HTML. Only used as a re-attach trigger — the listener has to be rebound when the
   * content changes, or it goes on pointing at replaced nodes.
   */
  html: string | null;
  /**
   * The AVSOS path of the content currently on screen, so a fragment link can be told apart from a
   * link to another document that happens to name an id this one also has.
   *
   * Pass the real path even on a surface that is not an article — Home passes its own content path.
   * Left undefined, every fragment-bearing citation navigates instead of scrolling in place.
   */
  currentPath?: string;
  /** A citation the proxy resolved. `path` is the raw `data-avs-citation` value, untouched. */
  onCitation: (path: string, label: string) => void;
  /** A reference that resolves to nothing — marked by AVSOS at ingest, or by prepareArticleHtml. */
  onDead: (hrefOrMarker: string, label: string) => void;
};

export function useCitationClicks({
  containerRef,
  html,
  currentPath,
  onCitation,
  onDead,
}: UseCitationClicksOptions): void {
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !html) return undefined;

    const scrollTo = (rawId: string): boolean => {
      if (!rawId) return false;
      const id = escapeSelectorValue(rawId);
      const target = el.querySelector(`[id="${id}"], [name="${id}"]`);
      if (!target) return false;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    };

    const handler = (e: Event) => {
      // LINK_ELEMENTS, not "a" — an <area> in an image map is a link, and for a map click
      // e.target IS the <area>, so closest() has to be told to look for it.
      const a = (e.target as Element).closest(LINK_ELEMENTS);
      if (!a) return;

      const label = (a.textContent || "").trim();

      // Resolved by the proxy — navigation to another knowledge-base document.
      const citationPath = a.getAttribute(CITATION_ATTR);
      if (citationPath) {
        e.preventDefault();
        /*
         * Scroll only when the fragment belongs to the document being read. Matching on the
         * fragment alone would scroll in place whenever a link to ANOTHER document happened to name
         * an id this one also has, silently swallowing the navigation.
         */
        const hashIdx = citationPath.indexOf("#");
        if (hashIdx > -1) {
          const samePath = citationPath.slice(0, hashIdx) === (currentPath || "").split("#")[0];
          if ((samePath || hashIdx === 0) && scrollTo(citationPath.slice(hashIdx + 1))) return;
        }
        onCitation(citationPath, label);
        return;
      }

      // Marked unresolvable — by AVSOS at ingest, or by prepareArticleHtml because nothing vouched
      // for the target. Either way it stays inside Copilot.
      const missing = a.getAttribute(DEAD_LINK_ATTR);
      if (missing !== null) {
        e.preventDefault();
        onDead(missing, label);
        return;
      }

      /*
       * Explicitly exempted by the server (e.g. Home's marketing CTAs, ACOP-120) — mirrors the same
       * carve-out in kbArticleHtml.ts's hardenAnchors/findEscapingHrefs. Without this, the fail-closed
       * tail below still owns this anchor: it has a real, non-"#" href and no CITATION_ATTR/
       * DEAD_LINK_ATTR, so isNavigatingHref(href) is true and the click would be preventDefault'd and
       * reported as dead — exactly the bug this attribute exists to fix. Let the browser's own click
       * behaviour run (opening the new tab), same as leaving a mailto: alone below.
       */
      if (a.hasAttribute(EXTERNAL_OK_ATTR)) return;

      const href = a.getAttribute("href");

      // No href attribute at all — a jump target such as <a id="top" name="top">, not a link.
      if (href === null) return;

      // In-page anchor. Also swallows a bare "#", which would otherwise jump the outer page.
      if (href.startsWith("#")) {
        e.preventDefault();
        scrollTo(href.slice(1));
        return;
      }

      // Nothing above claimed it and it can still navigate. Fail closed.
      if (isNavigatingHref(href)) {
        e.preventDefault();
        onDead(href, label);
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [containerRef, html, currentPath, onCitation, onDead]);
}
